import * as React from "react"
import { useSourceProviderTeamsForUserQuery } from "@modules/site/create/Import/queries.generated"
import { InstallationField } from "@modules/onboarding/createFromExisting/components/PickRepository.fields"
import { importSite as text } from "@modules/locales/default.js"
import {
  getCurrentSourceProviderTeam,
  RepoFormActionType,
  useRepoForm,
} from "@modules/site/create/Import/components/PickRepository.helpers"
import {
  SourceControlProvider,
  SourceOrganization,
} from "@modules/graphql/types"
import { useOrganizationNameFieldsFragment } from "@modules/organization/Settings/fragments.generated"
import useInstallationWindow from "@modules/toolkit/closable-window/useInstallationWindow"
import { SiteCreationRepositoryPicker } from "@modules/site/create/Import/components/SiteCreationRepositoryPicker"
import { InputFieldBlock } from "gatsby-interface"
import { NarrowSingleColumn } from "@modules/ui/layouts/Containers"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import Loading from "@modules/ui/components/Loading"
import { NoInstallations } from "@modules/site/create/shared/components/NoInstallations"
import { flexGrowCss } from "@modules/site/create/SiteCreationTypePicker"

type SiteCreateRepositoryProps = {
  selectedProvider: SourceControlProvider
  organizationId: string
  vcsConfigId: string
}

export function SiteCreationOrganizationPicker({
  selectedProvider,
  organizationId,
  vcsConfigId,
}: SiteCreateRepositoryProps) {
  const [hasClosedWindow, setHasClosedWindow] = React.useState(false)

  const { data, loading, error, refetch } = useSourceProviderTeamsForUserQuery({
    variables: {
      provider: selectedProvider,
      ...(vcsConfigId && { vcsConfigId }),
    },
    fetchPolicy: "network-only",
  })
  const sourceOrganizations = (data && data?.sourceProviderTeamsForUser) || []
  const { data: orgNameData } =
    useOrganizationNameFieldsFragment(organizationId)

  const [formState, dispatch] = useRepoForm()
  const selectedInstallation = getCurrentSourceProviderTeam(
    formState.installation,
    orgNameData?.name,
    sourceOrganizations as SourceOrganization[]
  )

  const { popInstallationWindow } = useInstallationWindow(
    (installationId: string) => {
      if (refetch) {
        /*
          when creating a new installation, 
          if a user unmounts the component before closing the window
          the refetch is no longer valid so we need to refetch only 
          when the component is still mounted
        */
        refetch()
      }

      if (installationId) {
        dispatch({
          type: RepoFormActionType.SelectInstallation,
          payload: installationId,
        })
      }

      setHasClosedWindow(true)
    },
    selectedProvider === SourceControlProvider.GithubEnterprise
      ? { vcsConfigId }
      : undefined
  )

  const [searchCriteria, setSearchCriteria] = React.useState("")

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchCriteria(e.target.value)
  }

  if (loading) {
    return (
      <Loading
        delay={1000}
        message={text.messages.loadingSourceProviderTeams}
        bufferSize="padded"
      />
    )
  }

  if (error) {
    return (
      <NarrowSingleColumn>
        <ErrorAlert>{error.message}</ErrorAlert>
      </NarrowSingleColumn>
    )
  }

  if (sourceOrganizations.length === 0 || !selectedInstallation) {
    return (
      <NoInstallations
        hasClosedWindow={hasClosedWindow}
        popInstallationWindow={popInstallationWindow}
        provider={selectedProvider}
        organizationId={organizationId}
        vcsConfigId={vcsConfigId}
        fromCreateSiteFlow={true}
      />
    )
  }

  return (
    <div
      css={theme => [
        flexGrowCss(theme),
        {
          width: "100%",
          height: "100%",
        },
      ]}
    >
      <div
        css={theme => ({
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          columnGap: theme.space[5],
          width: "100%",
        })}
      >
        <InstallationField
          label={""}
          sourceOrganizations={sourceOrganizations}
          selectedInstallation={selectedInstallation.id}
          onSelect={(value: string) => {
            dispatch({
              type: RepoFormActionType.SelectInstallation,
              payload: value,
            })
          }}
          popInstallationWindow={popInstallationWindow}
          provider={selectedProvider}
        />
        <InputFieldBlock
          id="repoSearch"
          label={""}
          onChange={handleSearchChange}
          placeholder={text.labels.searchRepositories}
          value={searchCriteria}
        />
      </div>
      <SiteCreationRepositoryPicker
        installationId={selectedInstallation.id}
        organizationId={organizationId}
        vcsConfigId={vcsConfigId}
        provider={selectedProvider}
        isUserTeam={!!selectedInstallation.isUserTeam}
        searchCriteria={searchCriteria}
      />
    </div>
  )
}
